import { ChangeDetectorRef, OnDestroy, Input, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYTService } from '../../../../../../../fikoyt.service';
import { FikoSecililer } from '../../FikoSecililer';

@Component({
  selector: 'fikoyt-undercut',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './undercut.component.html',
  styleUrls: ['./undercut.component.scss']
})
export class UndercutComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: any = null;

  subsList: Subscription[] = [];
  haritaOlaylar: Map<string, any> = new Map();

  // Undercut tespiti özellikleri
  shaderMaterial: THREE.ShaderMaterial | null = null;
  aktifMeshler: THREE.Mesh[] = [];
  orijinalMaterials: Map<THREE.Mesh, THREE.Material> = new Map();
  moldDirection: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
  analizAktif: boolean = false;

  // Yüzey seçimi
  yonSecimAktif: boolean = false;
  fikoSecimYuzey: any = null;
  secilenFaceNormal: THREE.Vector3 | null = null;

  // Undercut tespiti
  undercutTespitiAktif: boolean = true;

  // GPU-based undercut rendering
  private depthRenderTarget: THREE.WebGLRenderTarget | null = null;
  private depthCamera: THREE.OrthographicCamera | null = null;
  private depthScene: THREE.Scene | null = null;

  // Shader Lighting Parametreleri
  gelismisAyarlarAcik: boolean = false;

  // Light intensities
  keyLightIntensity: number = 0.6;
  fillLightIntensity: number = 0.4;
  rimLightIntensity: number = 0.3;
  ambientLight: number = 0.3;

  // Light positions
  keyLightX: number = 0.7;
  keyLightY: number = 1.0;
  keyLightZ: number = 0.5;

  fillLightX: number = -0.5;
  fillLightY: number = 0.3;
  fillLightZ: number = 0.5;

  rimLightX: number = 0.0;
  rimLightY: number = 0.0;
  rimLightZ: number = -1.0;

  // AO & Edge
  aoIntensity: number = 3.0;
  aoMax: number = 0.5;
  edgeIntensity: number = 2.0;
  edgeDarkness: number = 0.3;

  // Undercut Color
  undercutColorR: number = 0.0;
  undercutColorG: number = 0.5;
  undercutColorB: number = 1.0;
  undercutBrightness: number = 0.3;

  // Normal Surface Color
  normalColorR: number = 0.8;
  normalColorG: number = 0.8;
  normalColorB: number = 0.8;

  // Depth threshold
  depthThreshold: number = 0.001;

  constructor(public readonly FikoYT: FikoYTService, public readonly CD: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.shaderOlustur();
  }

  public baslat(anaGosterim?: any) {
    this.anaGosterim = anaGosterim;

    if (!this.shaderMaterial) {
      this.shaderOlustur();
    }

    // FikoOlcumler mesh'lerini raycast'ten kapat
    if (this.anaGosterim?.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
          child.userData.raycastable = false;
        }
      });
    }

    this.CD.detectChanges();
  }

  public bitir() {
    this.analiziDurdur();
    this.yuzeySecimiDurdur();

    // FikoOlcumler'leri geri raycast'e aç
    if (this.anaGosterim?.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
          child.userData.raycastable = true;
        }
      });
    }
  }

  private shaderOlustur() {
    this.shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        moldDirection: { value: this.moldDirection.clone() },
        undercutTespitiAktif: { value: this.undercutTespitiAktif },
        depthMap: { value: null },
        depthMatrixWorld: { value: new THREE.Matrix4() },
        depthProjectionMatrix: { value: new THREE.Matrix4() },
        depthViewMatrix: { value: new THREE.Matrix4() },
        // Lighting
        keyLightPos: { value: new THREE.Vector3(this.keyLightX, this.keyLightY, this.keyLightZ) },
        keyLightIntensity: { value: this.keyLightIntensity },
        fillLightPos: { value: new THREE.Vector3(this.fillLightX, this.fillLightY, this.fillLightZ) },
        fillLightIntensity: { value: this.fillLightIntensity },
        rimLightPos: { value: new THREE.Vector3(this.rimLightX, this.rimLightY, this.rimLightZ) },
        rimLightIntensity: { value: this.rimLightIntensity },
        ambientLight: { value: this.ambientLight },
        // AO & Edge
        aoIntensity: { value: this.aoIntensity },
        aoMax: { value: this.aoMax },
        edgeIntensity: { value: this.edgeIntensity },
        edgeDarkness: { value: this.edgeDarkness },
        // Colors
        undercutColor: { value: new THREE.Vector3(this.undercutColorR, this.undercutColorG, this.undercutColorB) },
        undercutBrightness: { value: this.undercutBrightness },
        normalColor: { value: new THREE.Vector3(this.normalColorR, this.normalColorG, this.normalColorB) },
        depthThreshold: { value: this.depthThreshold }
      },
      side: THREE.DoubleSide,
      transparent: true,
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vWorldNormal;
        varying vec3 vWorldPosition;
        varying vec4 vDepthPosition;
        varying vec3 vViewPosition;

        uniform mat4 depthProjectionMatrix;
        uniform mat4 depthViewMatrix;

        void main() {
            // View-space normal (lighting için)
            vNormal = normalize(normalMatrix * normal);

            // World position
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;

            // World normal (modelMatrix'ten scale hatasını kaldır)
            vWorldNormal = normalize(mat3(modelMatrix) * normal);

            // Depth camera'ya göre pozisyon
            vDepthPosition = depthProjectionMatrix * depthViewMatrix * worldPosition;

            // View space position
            vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
            vViewPosition = viewPosition.xyz;

            gl_Position = projectionMatrix * viewPosition;
        }

      `,
      fragmentShader: `
        uniform vec3 moldDirection;
uniform bool undercutTespitiAktif;
uniform sampler2D depthMap;

uniform vec3 keyLightPos;
uniform float keyLightIntensity;
uniform vec3 fillLightPos;
uniform float fillLightIntensity;
uniform vec3 rimLightPos;
uniform float rimLightIntensity;
uniform float ambientLight;

uniform float aoIntensity;
uniform float aoMax;
uniform float edgeIntensity;
uniform float edgeDarkness;

uniform vec3 undercutColor;
uniform float undercutBrightness;
uniform vec3 normalColor;
uniform float depthThreshold;

varying vec3 vNormal;
varying vec3 vWorldNormal;
varying vec3 vWorldPosition;
varying vec4 vDepthPosition;
varying vec3 vViewPosition;

// Float depth çözme
float unpackRGBAToDepth(vec4 v) {
    return dot(v, vec4(1.0, 1.0/255.0, 1.0/65025.0, 1.0/16581375.0));
}

void main() {

    vec3 worldNormal = normalize(vWorldNormal);
    vec3 viewNormal = normalize(vNormal);
    vec3 V = normalize(-vViewPosition);

    vec3 baseColor = normalColor;
    bool isUndercut = false;

    // Depth map kontrolü (her zaman çalıştır)
    {

        // NDC depth position
        vec3 ndc = vDepthPosition.xyz / vDepthPosition.w;

        // UV map
        vec2 uv = ndc.xy * 0.5 + 0.5;

        // UV ekran dışında mı?
        if (uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) {

            float mapDepth = unpackRGBAToDepth(texture2D(depthMap, uv));
            float curDepth = ndc.z * 0.5 + 0.5;

            // Undercut tespiti (ana mantık)
            if (mapDepth < curDepth - depthThreshold) {
                isUndercut = true;
                baseColor = undercutColor;
            }
        }
    }

    // ----------- LIGHTING --------------
    vec3 keyLight = normalize(keyLightPos);
    vec3 fillLight = normalize(fillLightPos);
    vec3 rimLight = normalize(rimLightPos);

    float keyDiffuse  = max(dot(viewNormal, keyLight), 0.0) * keyLightIntensity;
    float fillDiffuse = max(dot(viewNormal, fillLight), 0.0) * fillLightIntensity;
    float rimDiffuse  = max(dot(viewNormal, rimLight), 0.0) * rimLightIntensity;

    // ----------- AO / CURVATURE --------------
    vec3 dx = dFdx(vNormal);
    vec3 dy = dFdy(vNormal);
    float curvature = length(dx) + length(dy);

    float ao = 1.0 - clamp(curvature * aoIntensity, 0.0, aoMax);

    float totalLight = ambientLight + keyDiffuse + fillDiffuse + rimDiffuse;
    totalLight = clamp(totalLight * ao, ambientLight, 1.3);

    // Edge darken
    float edge = clamp(curvature * edgeIntensity, 0.0, 1.0);

    vec3 finalColor = baseColor * totalLight;
    finalColor = mix(finalColor, finalColor * (1.0 - edgeDarkness), edge);

    // Undercut ekstra parlaklık
    if (isUndercut) {
        finalColor = mix(finalColor, undercutColor * 1.4, undercutBrightness);
    }

    gl_FragColor = vec4(finalColor, 1.0);
}
      `
    });
  }

  public analiziBaslat() {
    const seciliMeshler = this.seciliMeshleriAl();
    if (!this.shaderMaterial || seciliMeshler.length === 0) {
      return;
    }

    this.analiziDurdur();

    seciliMeshler.forEach((mesh: THREE.Mesh) => {
      if (mesh instanceof THREE.Mesh && mesh.material) {
        this.orijinalMaterials.set(mesh, mesh.material as THREE.Material);

        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (!geometry.hasAttribute('color')) {
          const positionAttr = geometry.getAttribute('position');
          if (positionAttr) {
            const vertexCount = positionAttr.count;
            const colors = new Float32Array(vertexCount * 3);
            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          }
        }

        mesh.material = this.shaderMaterial;

        if (mesh.parent) {
          mesh.parent.userData.analizMeshParent = true;
        }

        this.aktifMeshler.push(mesh);
      }
    });

    this.analizAktif = true;

    // Lighting parametrelerini güncelle
    this.shaderParametreleriniGuncelle();

    // Undercut aktifse hesapla
    if (this.undercutTespitiAktif) {
      // setTimeout ile depth hesaplamasını bir sonraki frame'e ertele
      setTimeout(() => {
        this.undercutHesaplaGPU();
        this.anaGosterim?.ciz();
      }, 100);
    }

    this.CD.detectChanges();
  }

  public analiziDurdur() {
    this.aktifMeshler.forEach(mesh => {
      const orijinalMaterial = this.orijinalMaterials.get(mesh);
      if (orijinalMaterial) {
        mesh.material = orijinalMaterial;
      }

      if (mesh.parent && mesh.parent.userData?.analizMeshParent) {
        delete mesh.parent.userData.analizMeshParent;
      }
    });

    this.aktifMeshler = [];
    this.orijinalMaterials.clear();
    this.analizAktif = false;

    this.CD.detectChanges();
  }

  private seciliMeshleriAl(): THREE.Mesh[] {
    const meshler: THREE.Mesh[] = [];

    if (!this.anaGosterim) {
      return meshler;
    }

    const seciliGruplar = [];

    if (this.anaGosterim.secililer?.grupSecimlerUzerinde) {
      seciliGruplar.push(this.anaGosterim.secililer.grupSecimlerUzerinde);
    }
    if (this.anaGosterim.secililer?.grupSecimler2Uzerinde) {
      seciliGruplar.push(this.anaGosterim.secililer.grupSecimler2Uzerinde);
    }

    // Seçili gruplardaki SADECE FikoCizimYali'nin .mesh'ini al
    seciliGruplar.forEach((grup: any) => {
      if (grup && grup.children) {
        grup.children.forEach((child: any) => {
          // Sadece FikoCizimYali'nin mesh property'si varsa al
          if (child.mesh && child.mesh instanceof THREE.Mesh) {
            meshler.push(child.mesh);
          }
        });
      }
    });

    // Eğer hiç seçim yoksa, sahnedeki FikoCizimYali'lerin mesh'lerini al
    if (meshler.length === 0 && this.anaGosterim.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        // Sadece .mesh property'si olan objelerin mesh'ini al (FikoCizimYali)
        if (child.mesh && child.mesh instanceof THREE.Mesh && child.mesh.visible) {
          meshler.push(child.mesh);
        }
      });
    }

    return meshler;
  }

  public moldYonunuGuncelle(eksen: 'x' | 'y' | 'z') {
    this.secilenFaceNormal = null;

    switch (eksen) {
      case 'x':
        this.moldDirection = new THREE.Vector3(1, 0, 0);
        break;
      case 'y':
        this.moldDirection = new THREE.Vector3(0, 1, 0);
        break;
      case 'z':
        this.moldDirection = new THREE.Vector3(0, 0, 1);
        break;
    }

    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
    }

    if (this.undercutTespitiAktif) {
      this.undercutHesaplaGPU();
    }

    this.CD.detectChanges();
  }

  public yonSec() {
    this.yuzeySecimiBaslat();
  }

  public resetYuzeySecimi() {
    this.secilenFaceNormal = null;
    this.moldDirection = new THREE.Vector3(0, 0, 1);

    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
    }

    this.yuzeySecimiBaslat();
    this.CD.detectChanges();
  }

  public undercutTespitiToggle() {
    this.undercutTespitiAktif = !this.undercutTespitiAktif;
    this.CD.detectChanges();

    if (this.undercutTespitiAktif) {
      this.undercutHesaplaGPU();
    } else {
      if (this.depthRenderTarget) {
        this.depthRenderTarget.dispose();
        this.depthRenderTarget = null;
      }
      if (this.shaderMaterial) {
        this.shaderMaterial.uniforms.depthMap.value = null;
      }
    }

    this.shaderiGuncelle();
    this.anaGosterim?.ciz();
  }

  private undercutHesaplaGPU() {
    const seciliMeshler = this.seciliMeshleriAl();

    if (seciliMeshler.length === 0 || !this.anaGosterim?.renderer) {
      return;
    }

    // Bounding box corners topla (Ters Açı Analizi mantığı)
    const allCorners: THREE.Vector3[] = [];

    seciliMeshler.forEach(mesh => {
      if (mesh.geometry) {
        const geometry = mesh.geometry as THREE.BufferGeometry;

        if (!geometry.boundingBox) {
          geometry.computeBoundingBox();
        }

        if (!geometry.boundingBox) {
          return;
        }

        const localBox = geometry.boundingBox;

        const corners = [
          new THREE.Vector3(localBox.min.x, localBox.min.y, localBox.min.z),
          new THREE.Vector3(localBox.min.x, localBox.min.y, localBox.max.z),
          new THREE.Vector3(localBox.min.x, localBox.max.y, localBox.min.z),
          new THREE.Vector3(localBox.min.x, localBox.max.y, localBox.max.z),
          new THREE.Vector3(localBox.max.x, localBox.min.y, localBox.min.z),
          new THREE.Vector3(localBox.max.x, localBox.min.y, localBox.max.z),
          new THREE.Vector3(localBox.max.x, localBox.max.y, localBox.min.z),
          new THREE.Vector3(localBox.max.x, localBox.max.y, localBox.max.z)
        ];

        corners.forEach(corner => {
          corner.applyMatrix4(mesh.matrixWorld);
          allCorners.push(corner);
        });
      }
    });

    if (allCorners.length === 0) {
      return;
    }

    // Center: Tüm corner'ların ortalaması (Ters Açı Analizi mantığı)
    const center = new THREE.Vector3();
    allCorners.forEach(corner => {
      center.add(corner);
    });
    center.divideScalar(allCorners.length);

    const moldDirNorm = this.moldDirection.clone().normalize();

    // Up vector seçimi (Ters Açı Analizi mantığı)
    const dotY = Math.abs(moldDirNorm.dot(new THREE.Vector3(0, 1, 0)));
    const dotX = Math.abs(moldDirNorm.dot(new THREE.Vector3(1, 0, 0)));
    const dotZ = Math.abs(moldDirNorm.dot(new THREE.Vector3(0, 0, 1)));

    let upVector: THREE.Vector3;
    if (dotX < dotY && dotX < dotZ) {
      upVector = new THREE.Vector3(1, 0, 0);
    } else if (dotZ < dotY) {
      upVector = new THREE.Vector3(0, 0, 1);
    } else {
      upVector = new THREE.Vector3(0, 1, 0);
    }

    const rightVector = new THREE.Vector3().crossVectors(moldDirNorm, upVector).normalize();
    upVector = new THREE.Vector3().crossVectors(rightVector, moldDirNorm).normalize();

    // Projection: Corner'ları center'dan çıkarıp project et (Ters Açı Analizi mantığı)
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    allCorners.forEach(corner => {
      const projected = corner.clone().sub(center);
      const x = projected.dot(rightVector);
      const y = projected.dot(upVector);
      const z = projected.dot(moldDirNorm);

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    });

    // Margin ekle (Ters Açı Analizi mantığı)
    const xyMargin = Math.max(maxX - minX, maxY - minY) * 0.25;
    minX -= xyMargin;
    maxX += xyMargin;
    minY -= xyMargin;
    maxY += xyMargin;

    const zMargin = (maxZ - minZ) * 0.3;
    minZ -= zMargin;
    maxZ += zMargin;

    const zRange = maxZ - minZ;
    const cameraDistance = zRange * 0.6 + Math.max(zRange, 60);

    // Near/far plane (Ters Açı Analizi formülü - X/Y'de çalışıyor)
    const nearPlane = Math.max(0.5, cameraDistance * 0.02);
    const farPlane = zRange + cameraDistance * 2.0;

    this.depthCamera = new THREE.OrthographicCamera(
      minX, maxX,
      maxY, minY,
      nearPlane, farPlane
    );

    // Tüm eksenlerde aynı: ters açı analizi gibi
    this.depthCamera.position.copy(center).add(
      moldDirNorm.clone().multiplyScalar(cameraDistance)
    );

    this.depthCamera.up.copy(upVector);
    this.depthCamera.lookAt(center);
    this.depthCamera.updateMatrixWorld();

    if (this.depthRenderTarget) {
      this.depthRenderTarget.dispose();
    }

    this.depthRenderTarget = new THREE.WebGLRenderTarget(2048, 2048, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType
    });

    this.depthScene = new THREE.Scene();
    const depthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
      side: THREE.DoubleSide
    });

    seciliMeshler.forEach(mesh => {
      if (mesh.type === 'Mesh' && mesh.geometry) {
        mesh.updateMatrixWorld(true);

        // Geometry'yi clone ve world transform uygula
        const geometry = (mesh.geometry as THREE.BufferGeometry).clone();
        geometry.applyMatrix4(mesh.matrixWorld);

        const clonedMesh = new THREE.Mesh(geometry, depthMaterial);
        this.depthScene!.add(clonedMesh);
      }
    });

    this.depthScene.updateMatrixWorld(true);


    let renderer: any = null;

    const possiblePaths = [
      this.anaGosterim.webGLRenderer,
      this.anaGosterim.threeRenderer,
      this.anaGosterim.renderer3d,
      this.anaGosterim._renderer
    ];

    for (const possibleRenderer of possiblePaths) {
      if (possibleRenderer && typeof possibleRenderer.getRenderTarget === 'function') {
        renderer = possibleRenderer;
        break;
      }
    }

    if (!renderer) {
      for (const key in this.anaGosterim) {
        const value = (this.anaGosterim as any)[key];
        if (value && typeof value.getRenderTarget === 'function') {
          renderer = value;
          break;
        }
      }
    }

    if (!renderer || typeof renderer.getRenderTarget !== 'function') {
      return;
    }

    const currentRenderTarget = renderer.getRenderTarget();

    renderer.setRenderTarget(this.depthRenderTarget);
    renderer.clear();
    renderer.render(this.depthScene, this.depthCamera);
    renderer.setRenderTarget(currentRenderTarget);

    // DEBUG plane'i kaldır
    if (this.anaGosterim?.sahneler?.[0]) {
      const oldDebugPlane = this.anaGosterim.sahneler[0].getObjectByName('DEBUG_DEPTH_PLANE');
      if (oldDebugPlane) {
        this.anaGosterim.sahneler[0].remove(oldDebugPlane);
      }
    }

    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.depthMap.value = this.depthRenderTarget.texture;
      this.shaderMaterial.uniforms.depthProjectionMatrix.value = this.depthCamera.projectionMatrix;
      this.shaderMaterial.uniforms.depthViewMatrix.value = this.depthCamera.matrixWorldInverse;

      // Depth threshold'u dinamik ayarla (Z range'e göre)
      const dynamicThreshold = Math.min(0.001, zRange * 0.00001);
      this.shaderMaterial.uniforms.depthThreshold.value = dynamicThreshold;

      // Lighting parametrelerini güncelle
      this.shaderParametreleriniGuncelle();
    }

    // Render'ı tetikle
    if (this.anaGosterim) {
      this.anaGosterim.ciz();
    }

    this.CD.detectChanges();
  }

  private shaderiGuncelle() {
    if (!this.shaderMaterial) return;

    this.shaderMaterial.uniforms.undercutTespitiAktif.value = this.undercutTespitiAktif;

    // Lighting parametrelerini de güncelle
    this.shaderParametreleriniGuncelle();

    this.CD.detectChanges();
  }

  public shaderParametreleriniGuncelle() {
    if (!this.shaderMaterial) return;

    // Lighting
    this.shaderMaterial.uniforms.keyLightPos.value.set(this.keyLightX, this.keyLightY, this.keyLightZ);
    this.shaderMaterial.uniforms.keyLightIntensity.value = this.keyLightIntensity;
    this.shaderMaterial.uniforms.fillLightPos.value.set(this.fillLightX, this.fillLightY, this.fillLightZ);
    this.shaderMaterial.uniforms.fillLightIntensity.value = this.fillLightIntensity;
    this.shaderMaterial.uniforms.rimLightPos.value.set(this.rimLightX, this.rimLightY, this.rimLightZ);
    this.shaderMaterial.uniforms.rimLightIntensity.value = this.rimLightIntensity;
    this.shaderMaterial.uniforms.ambientLight.value = this.ambientLight;

    // AO & Edge
    this.shaderMaterial.uniforms.aoIntensity.value = this.aoIntensity;
    this.shaderMaterial.uniforms.aoMax.value = this.aoMax;
    this.shaderMaterial.uniforms.edgeIntensity.value = this.edgeIntensity;
    this.shaderMaterial.uniforms.edgeDarkness.value = this.edgeDarkness;

    // Colors
    this.shaderMaterial.uniforms.undercutColor.value.set(this.undercutColorR, this.undercutColorG, this.undercutColorB);
    this.shaderMaterial.uniforms.undercutBrightness.value = this.undercutBrightness;
    this.shaderMaterial.uniforms.normalColor.value.set(this.normalColorR, this.normalColorG, this.normalColorB);
    this.shaderMaterial.uniforms.depthThreshold.value = this.depthThreshold;

    this.CD.detectChanges();
  }

  public varsayilanDegerleriYukle() {
    // Light intensities
    this.keyLightIntensity = 0.6;
    this.fillLightIntensity = 0.4;
    this.rimLightIntensity = 0.3;
    this.ambientLight = 0.3;

    // Light positions
    this.keyLightX = 0.7;
    this.keyLightY = 1.0;
    this.keyLightZ = 0.5;

    this.fillLightX = -0.5;
    this.fillLightY = 0.3;
    this.fillLightZ = 0.5;

    this.rimLightX = 0.0;
    this.rimLightY = 0.0;
    this.rimLightZ = -1.0;

    // AO & Edge
    this.aoIntensity = 3.0;
    this.aoMax = 0.5;
    this.edgeIntensity = 2.0;
    this.edgeDarkness = 0.3;

    // Undercut Color
    this.undercutColorR = 0.0;
    this.undercutColorG = 0.5;
    this.undercutColorB = 1.0;
    this.undercutBrightness = 0.3;

    // Normal Surface Color
    this.normalColorR = 0.8;
    this.normalColorG = 0.8;
    this.normalColorB = 0.8;

    // Depth threshold
    this.depthThreshold = 0.001;

    this.shaderParametreleriniGuncelle();
  }

  // RGB to Hex converter
  public rgbToHex(r: number, g: number, b: number): string {
    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }

  // Hex to RGB converter
  public hexToRgb(hex: string, type: 'undercut' | 'normal'): void {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16) / 255;
      const g = parseInt(result[2], 16) / 255;
      const b = parseInt(result[3], 16) / 255;

      if (type === 'undercut') {
        this.undercutColorR = r;
        this.undercutColorG = g;
        this.undercutColorB = b;
      } else {
        this.normalColorR = r;
        this.normalColorG = g;
        this.normalColorB = b;
      }

      this.shaderParametreleriniGuncelle();
    }
  }

  private yuzeySecimiBaslat() {
    if (!this.anaGosterim || this.fikoSecimYuzey?.aktif) {
      return;
    }

    const seciliMeshler = this.seciliMeshleriAl();
    if (seciliMeshler.length === 0) {
      return;
    }

    if (this.fikoSecimYuzey) {
      this.fikoSecimYuzey.bitir();
      this.fikoSecimYuzey = null;
    }

    this.fikoSecimYuzey = new FikoSecililer(this.anaGosterim);
    this.fikoSecimYuzey.rayObje = seciliMeshler;
    this.fikoSecimYuzey.filitreler = ["Mesh"];
    this.fikoSecimYuzey.raycaster.params.Mesh = { threshold: 0 };

    const sub = this.fikoSecimYuzey.tiklandi.subscribe((kesisim: any) => {
      let faceNormal: THREE.Vector3 | null = null;
      const actualMesh = kesisim?.altObje || kesisim?.object;
      const mesh = actualMesh as THREE.Mesh;

      if (kesisim?.face?.normal) {
        const localNormal = kesisim.face.normal.clone();
        faceNormal = localNormal.applyMatrix3(
          new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
        ).normalize();
      } else if (mesh.geometry && kesisim.faceIndex !== undefined) {
        const geometry = mesh.geometry as THREE.BufferGeometry;
        const normalAttr = geometry.getAttribute('normal') as THREE.BufferAttribute;
        if (normalAttr) {
          const faceIndex = kesisim.faceIndex;
          const normalArray = new Float32Array(3);
          normalArray[0] = normalAttr.getX(faceIndex);
          normalArray[1] = normalAttr.getY(faceIndex);
          normalArray[2] = normalAttr.getZ(faceIndex);
          const localNormal = new THREE.Vector3(normalArray[0], normalArray[1], normalArray[2]);
          faceNormal = localNormal.applyMatrix3(
            new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
          ).normalize();
        }
      }

      if (faceNormal) {
        this.secilenFaceNormal = faceNormal.clone();
        this.moldDirection = faceNormal.clone();

        if (this.shaderMaterial) {
          this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
        }

        if (this.undercutTespitiAktif) {
          this.undercutHesaplaGPU();
        }

        this.yuzeySecimiDurdur();
        this.CD.detectChanges();
      }
    });

    this.subsList.push(sub);

    this.fikoSecimYuzey.baslat();
    this.fikoSecimYuzey.aktif = true;

    this.yonSecimAktif = true;
    this.CD.detectChanges();
  }

  private yuzeySecimiDurdur() {
    if (this.fikoSecimYuzey) {
      this.fikoSecimYuzey.aktif = false;
      this.fikoSecimYuzey.bitir();
      this.fikoSecimYuzey = null;
    }
    this.yonSecimAktif = false;
    this.CD.detectChanges();
  }

  public yonuTersineCevir() {
    this.moldDirection.multiplyScalar(-1);

    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
    }

    if (this.undercutTespitiAktif) {
      this.undercutHesaplaGPU();
    }

    this.CD.detectChanges();
  }

  ngOnDestroy(): void {
    this.analiziDurdur();

    if (this.shaderMaterial) {
      this.shaderMaterial.dispose();
      this.shaderMaterial = null;
    }

    if (this.depthRenderTarget) {
      this.depthRenderTarget.dispose();
      this.depthRenderTarget = null;
    }

    if (this.depthScene) {
      this.depthScene.clear();
      this.depthScene = null;
    }

    this.orijinalMaterials.clear();
    this.aktifMeshler = [];

    this.subsList.forEach(element => {
      if (element && element.unsubscribe) {
        element.unsubscribe();
      }
    });

    this.haritaOlaylar.forEach((value: any, key: string) => {
      if (value && typeof value === 'function') {
        value();
      }
    });
    this.haritaOlaylar.clear();

    this.anaGosterim = null;
    this.secilenFaceNormal = null;
  }
}
